/**
 * Learner registration.
 *
 * Uses the v2 registration endpoint, which returns field-level errors keyed by field
 * name. Those are mapped back onto the inputs rather than shown as one blob, because
 * "this username is taken" belongs next to the username.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Banner, Button, Field } from '../../shared/ui';
import { getConfig, text } from '../../shared/lib/config';
import { openedx, ApiError } from '../../shared/lib/openedx';
import { loadAuthContext, EMPTY_AUTH_CONTEXT, type AuthContext } from '../../shared/lib/providers';
import { ProviderButtons } from './ProviderButtons';
import './auth.css';

/** Open edX returns {field: [{user_message}]}; flatten to {field: message}. */
function fieldErrors(body: unknown): Record<string, string> {
  if (!body || typeof body !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [field, value] of Object.entries(body as Record<string, unknown>)) {
    if (Array.isArray(value) && value.length > 0) {
      const first = value[0] as { user_message?: string };
      if (first?.user_message) out[field] = first.user_message;
    } else if (typeof value === 'string') {
      out[field] = value;
    }
  }
  return out;
}

export default function Register() {
  const config = getConfig();
  const [params] = useSearchParams();
  const next = params.get('next') ?? '/dashboard';

  const [auth, setAuth] = useState<AuthContext>(EMPTY_AUTH_CONTEXT);
  const [form, setForm] = useState({ name: '', email: '', username: '', password: '' });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadAuthContext(next).then(setAuth);
  }, [next]);

  function set(field: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm({ ...form, [field]: e.target.value });
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setErrors({});

    try {
      await openedx.postForm('/api/user/v2/account/registration/', {
        name: form.name,
        email: form.email,
        username: form.username,
        password: form.password,
        // Open edX requires explicit agreement; the checkbox is the consent, this is
        // the field it maps to.
        honor_code: 'true',
      });
      window.location.assign(next);
    } catch (err) {
      if (err instanceof ApiError) {
        const mapped = fieldErrors(err.body);
        setErrors(
          Object.keys(mapped).length > 0
            ? mapped
            : { form: 'We could not create the account. Please check your details.' },
        );
      } else {
        setErrors({ form: 'Registration is unavailable right now.' });
      }
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <aside className="auth__visual">
        <div className="auth__logo">{config.tenant.name}</div>
        <div className="auth__pitch">
          <h2>{text('hero_heading', 'Learn something new')}</h2>
          <p>{text('hero_body', '')}</p>
        </div>
      </aside>

      <section className="auth__pane">
        <div className="auth__card">
          <p className="auth__eyebrow">Join {config.tenant.name}</p>
          <h1 className="auth__title">Create your account</h1>

          {errors.form && <Banner tone="danger">{errors.form}</Banner>}

          <form className="auth__form" onSubmit={submit} noValidate>
            <Field
              label="Full name"
              name="name"
              autoComplete="name"
              value={form.name}
              onChange={set('name')}
              error={errors.name}
              required
            />
            <Field
              label="Email"
              name="email"
              type="email"
              autoComplete="email"
              value={form.email}
              onChange={set('email')}
              error={errors.email}
              required
            />
            <Field
              label="Username"
              name="username"
              autoComplete="username"
              value={form.username}
              onChange={set('username')}
              error={errors.username}
              hint="Public. Shown on certificates and in discussions."
              required
            />
            <Field
              label="Password"
              name="password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={set('password')}
              error={errors.password}
              required
            />
            <Button type="submit" variant="primary" busy={busy}>
              Create account
            </Button>
          </form>

          <ProviderButtons providers={auth.providers} mode="register" />

          <p className="auth__alt">
            Already have an account? <Link to={`/login?next=${encodeURIComponent(next)}`}>Sign in</Link>
          </p>
        </div>
      </section>
    </main>
  );
}
